import { defineStore } from 'pinia';
import { useCookieBonusType } from '~/composables/useCookieBonusType';
import { useCookieStag } from '~/composables/useCookieStag';
import { useCookieUserType } from '~/composables/useCookieUserType';
import { useStoreComponents } from '~/stores/strapi/components';
import { EDropdownName } from '~/types/dropdowns';
import { ELoadingStatus } from '~/types/loadingStatus';
import { ETriggerId } from '~/types/toasts';
import {
  BONUS_ABUSER_GROUP,
  COMMON_REQUEST_INTERVAL,
  DEFAULT_CURRENCIES,
  PROMO_TYPE_GROUPS,
  STORAGE_BONUS_ABUSER_KEY,
} from '~/utils/constants';
import { initOneSignal } from '~/utils/oneSignal';
import { errorsParser } from '~/utils/ss/errorsParser';
import { useStoreNotifications } from '../abstract/notifications';
import { useOverlayQueueStore } from '../abstract/overlayQueue';
import { useStoreRoot } from '../abstract/root';
import { useStoreCountry } from '../client/country';
import { useStoreSeoReferrer } from '../client/seoReferrer';
import { useStoreSessionTimer } from '../client/sessionTimer';
import { useStoreStag } from '../client/stag';
import { useStorePaymentsNotification } from '../services/paymentsNotification';
import { useStoreSeon } from '../services/seon';
import { useStoreCrossSalePopup } from '../strapi/crossSalePopup';
import { useStoreCurrencyMenu } from '../strapi/currencyMenu';
import { useStoreModalProfile } from '../strapi/modals/profile';
import { useStoreToasts } from '../strapi/toasts';
import { useStoreVipLevelData } from '../strapi/vipLevelData';
import { softswiss } from '../transport/softswiss';
import { useStoreSsAccounts } from './accounts';
import { useStoreSsCentrifuge } from './centrifuge';
import { useStoreSsGamesFavourite } from './gamesFavourite';
import { useStoreSsLimits } from './limits';
import { useStoreSsLocales } from './locales';
import { useStoreSsLootboxes } from './lootboxes';
import { useStoreSsPaymentMethods } from './paymentMethods';
import { useStoreSsPlayerBonuses } from './playerBonuses';
import { useStoreSsPlayerPayments } from './playerPayments';
import { useStoreSsTournaments } from './tournaments';
import { useStoreTransactionHistory } from './transactionHistory';
import type { ISsCentrifugeGroupsChannel } from '~/types/softswiss/ssCentrifuge';
import type {
  IOAuthProvider,
  IOAuthRoutes,
  ISsPlayerDocument,
  ISsPlayerEmailConfirmationResponse,
  ISsPlayerLimits,
  ISsPlayerLogin,
  ISsPlayerLoginRequest,
  ISsPlayerLoginResponse,
  ISsPlayerRegister,
  ISsPlayerRegistrationRequest,
  ISsPlayerRegistrationResponse,
  ISsPlayerResetPassword,
  ISsPlayerResetResponse,
  ISsPlayerResponse,
  ISsPlayerSession,
  ISsPlayerSettings,
  ISsPlayerSubscriptions,
  ISsPlayerTwoFactor,
  ISsPlayerUploadDocument,
  ISsserGroupsUpdateRequestInterface,
  tSsPlayerDocumentUploadResponse,
} from '~/types/softswiss/ssPlayer';
import type { ISsPlayerBalance } from '~/types/softswiss/ssPlayerBalance';
import type { ISsPlayerCompPoints } from '~/types/softswiss/ssPlayerCompPoints';
import type {
  ISsPlayerProfileUpdate,
  ISsPlayerProfileUpdateRequest,
} from '~/types/softswiss/ssPlayerProfileUpdate';
import type { IHttpRequestOptions } from '~/types/transport/http';

export const useStoreSsPlayer = defineStore('ssPlayer', {
  state: () => ({
    playerInfo: {} as ISsPlayerResponse,
    playerPhoneInfo: {} as {
      phone_country: string;
      phone_number: string;
      sms_limit_reached: boolean;
      verified_status: string;
      resend_timeout_until: string | null;
    },
    playerCompPoints: {} as ISsPlayerCompPoints,
    playerLevels: [] as {
      group_id: string;
      level: number;
      compoints: number;
    }[],
    playerSettings: {} as ISsPlayerSettings,
    lastUpdateCompointsTime: NaN,
    intervalId: NaN,
    requestPromise: null as Promise<any> | null,
    requestCompointsPromise: null as Promise<any> | null,
    status: ELoadingStatus.noData as ELoadingStatus,
    statusCompoints: ELoadingStatus.noData as ELoadingStatus,
    playerInfoStatus: ELoadingStatus.noData as ELoadingStatus,
    playerInfoPromise: null as null | Promise<ISsPlayerResponse>,
    oAuthRoutes: {} as IOAuthRoutes,
    firstLoad: false as boolean,
    authStatusEarlyLoad: null as null | number | boolean,
    hasAuthToken: false,
    playerHasAbuserStatus: false,
    playerDocuments: [] as ISsPlayerDocument[],
    isDocumentsLoadedOnce: false,
    playerTwoFactorData: {} as ISsPlayerTwoFactor,
    playerTwoFactorBackupCodes: [] as string[],
    playerSessions: [] as ISsPlayerSession[],
    playerSubscriptionsStatuses: {} as ISsPlayerSubscriptions,
  }),

  getters: {
    isPlayerDataLoadedOnce(state): boolean {
      return !!state.lastUpdateCompointsTime;
    },

    isPlayerDataLoadingFirstInitOnce(state): boolean {
      return state.firstLoad;
    },

    playerCurrency(state): string | null {
      return (state.playerInfo?.currency as string) || null;
    },

    isFiatCurrency(state): boolean {
      return (
        !!state.playerInfo?.currency &&
        !useStoreCurrencyMenu().cryptoCurrencyCodes?.includes(state.playerInfo?.currency)
      );
    },

    playerId(state): string | null {
      return (state.playerInfo?.id?.toString() as string) || null;
    },

    isPlayerAuthorised(state): boolean {
      // return !!state.authStatusEarlyLoad || !!state.playerInfo.id;
      return !!state.playerInfo.id;
    },

    isPlayerAuthorisedEarly(state) {
      return state.authStatusEarlyLoad || !!state.playerInfo.id;
    },

    playerCompPointsNumber(): number {
      return this.playerCompPoints?.persistent?.points || 0;
    },

    playerChargeableCompPoints(): number {
      return this.playerCompPoints?.chargeable?.points || 0;
    },

    isDepositReady(state): boolean {
      const requiredFields = useStoreModalProfile()
        .fieldsList.filter((field) => field.paymentRequired)
        .map((field) => field.type);

      return requiredFields.every((field) => !!(state.playerInfo as any)[field]);
    },

    playerGroupIds(state): string[] {
      if (state.playerInfo?.statuses) {
        return state.playerInfo?.statuses.map((ps) => ps.id);
      }

      return [];
    },

    playerCountry(state): string {
      if (state.playerInfo?.country) {
        return state.playerInfo?.country || '';
      }

      return useStoreCountry().country || '';
    },

    playerLevel(): number {
      const points = this.playerCompPointsNumber;
      const levels = useStoreVipLevelData()
        .data.filter((l) => l.compoints <= points)
        .sort((a, b) => b.level - a.level);

      return levels[0] ? levels[0].level : 1;
    },

    googleOAuthUrl(): string {
      if (!this.oAuthRoutes.google_oauth2) {
        return '';
      }
      return window
        ? `${window.location.protocol}//www.${window.location.host}${this.oAuthRoutes.google_oauth2}`
        : '';
    },
    isWithFbFlowSignup() {
      const platform = usePlatform();
      return useStoreRoot().isFbTypeApp && platform.isMobile;
    },
    isFbSignupFlowShown(): boolean {
      const platform = usePlatform();
      return (
        useStoreRoot().isFbTypeApp &&
        !this.hasAuthToken &&
        !this.isPlayerAuthorised &&
        platform.isMobile
      );
    },
    isPlayerInAbuserGroup(): boolean {
      if (!this.isPlayerAuthorised) {
        return false;
      }
      return (
        this.playerGroupIds.includes(BONUS_ABUSER_GROUP) ||
        (window && Boolean(window.sessionStorage.getItem(STORAGE_BONUS_ABUSER_KEY)))
      );
    },
    isPlayerCryptoCountry(): boolean {
      return (
        useStoreCountry().isCurrentCountryCrypto &&
        !this.playerDocuments.some((d) => d.document_type === 'address' && d.status === 'approved')
      );
    },
  },

  actions: {
    async loadCommonData(options?: { isAfterRegistration?: boolean }) {
      if (this.playerInfo?.id) {
        await Promise.all([
          useStoreSsAccounts().loadPlayerBalance({ force: true }),
          useStoreSsPlayer().loadUserCompPoints(),
          useStoreSsPlayer().loadUserSettings({
            force: true,
            isAfterRegistration: options?.isAfterRegistration,
          }),
          useStoreSsGamesFavourite().load({ force: true }),
          ...(this.isDocumentsLoadedOnce ? [] : [this.getPlayerDocuments()]),
          useStoreTransactionHistory().loadPayments(),
        ]);

        useStoreNotifications().subscribe(); // timeout request for checking notifications created in strapi
        useStoreSsPlayerBonuses().startCheckingUpdates(); // timeout request for checking bonuses if no updates from centrifuge
        useStoreSsPlayer().startCheckingCompointsUpdates(); // timeout request for checking compoints if no updates from centrifuge
        useStoreSsPlayerPayments().startCheckingUpdates(); // timeout request for checking payments if no updates from centrifuge
        useStoreSsCentrifuge().subscribeChannels(); // subscribe on SS WS centrifuge
        useCookieUserType().check();
        useStoreSessionTimer().start();
        useStoreSsLimits().getUserCurrentLimits();
        useStoreSsLootboxes().loadLootboxes();
        localStorage.setItem(HAD_SIGNIN_STORAGE_KEY, 'true');
      }
    },

    async initPlayerLoad() {
      await this.loadCommonData();
      this.firstLoad = true;

      if (this.playerInfo.id) {
        initOneSignal();
        useStoreCrossSalePopup().syncPopups();
        useStoreToasts().loadToasts();
        useStorePaymentsNotification().subscribe(this.playerInfo.id);
        this.status = ELoadingStatus.loaded;
      } else {
        this.status = ELoadingStatus.error;
      }
    },

    fetchPlayerData() {
      if (!this.requestPromise) {
        this.requestPromise = softswiss()
          .get({ path: '/api/player', force: true })
          .finally(() => (this.requestPromise = null));
      }

      return this.requestPromise;
    },

    loadEarlyAuthStatus() {
      const authData = this.getPlayerInfoFromLocalStorage();

      if (authData) {
        this.playerInfo = authData;
      }
      this.authStatusEarlyLoad = !!authData;
    },

    setHasAuthToken(payload: boolean = false) {
      this.hasAuthToken = payload;
    },

    async load(): Promise<any> {
      if (!import.meta.browser) {
        console.error('SSR BLOCK! action ssPlayer/load can be call only from browser!');
        return;
      }

      if (this.playerInfoPromise) {
        return this.playerInfoPromise;
      }

      await this.initPlayerLoad();

      this.playerInfoPromise = this.fetchPlayerData().then((playerInfo) => {
        this.playerInfo = { ...this.playerInfo, ...playerInfo };
        this.savePlayerInfoToLocalStorage(playerInfo);
        this.playerInfoPromise = null;
      }) as Promise<ISsPlayerResponse>;
    },

    async loadPlayerInfo() {
      if (!import.meta.browser) {
        console.error('SSR BLOCK! action ssPlayer/loadPlayerInfo can be call only from browser!');
        return;
      }
      this.playerInfoStatus = ELoadingStatus.loading;

      // const playerInfo: ISsPlayerResponse = await resolveAfter2Seconds(10);
      const playerInfo: ISsPlayerResponse = await this.fetchPlayerData();

      this.playerInfo = { ...this.playerInfo, ...playerInfo };
      this.playerInfoStatus = ELoadingStatus.loaded;
    },

    async resetPassword(data: ISsPlayerResetPassword) {
      const playerResetPassword = await softswiss().put<ISsPlayerResetResponse>({
        path: '/api/users/password',
        force: true,
        payload: { user: data },
        getErrors: true,
      });

      if (playerResetPassword?.errors) {
        return { errors: playerResetPassword.errors };
      } else if (playerResetPassword) {
        return playerResetPassword;
      } else {
        return {};
      }
    },

    /**
     * Action for sending request to SS API for reset user's password
     * if user don't remember password or saving errors from API response
     *
     * @param email user's email
     */
    async forgot(email: string) {
      const playerForgot = await softswiss().post<ISsPlayerResetResponse>({
        path: '/api/users/password',
        force: true,
        payload: { user: { email } },
        getErrors: true,
      });

      if (playerForgot && playerForgot.errors) {
        return { errors: playerForgot.errors };
      } else if (playerForgot) {
        playerForgot.sendEmail = true;

        return playerForgot;
      } else {
        return { errors: { email: { error: 'unknown error' } } };
      }
    },

    /**
     * Action for sending request to SS API and unlock user's account
     * if it was blocked or saving errors from API response
     *
     * @param email user's email
     */
    async unlock(email: string) {
      const playerUnlock = await softswiss().post<ISsPlayerResetResponse>({
        path: '/api/users/unlock',
        force: true,
        payload: { user: { email } },
        getErrors: true,
      });

      if (playerUnlock?.errors) {
        return { errors: playerUnlock.errors };
      } else if (playerUnlock) {
        playerUnlock.sendEmail = true;

        return playerUnlock;
      } else {
        return { errors: { email: { error: 'unknown error' } } };
      }
    },

    /**
     * Action for sending request to SS API and changing user status to loginned
     * if get response with player info, or saving errors if get errors
     *
     * @param playerData user's email, password and captcha token     *
     */
    async login(
      playerData: ISsPlayerLogin,
      headers?: IHttpRequestOptions['headers']
    ): Promise<ISsPlayerResponse | ISsPlayerLoginResponse> {
      this.status = ELoadingStatus.loading;
      const playerInfo = await softswiss().post<ISsPlayerResponse>({
        path: '/api/users/sign_in',
        force: true,
        payload: { user: playerData } as ISsPlayerLoginRequest,
        getErrors: true,
        getRequired: true,
        headers,
      });

      if (playerInfo?.id) {
        softswiss().CLEAR_DATA();
        this.playerInfo = playerInfo;
        this.savePlayerInfoToLocalStorage(playerInfo);
        await this.loadCommonData();
        this.status = ELoadingStatus.loaded;

        if (playerInfo?.currency) {
          useStoreRoot().SET_USER_CURRENCY(playerInfo?.currency);
        }

        this.checkEstonianLicenceFromProfileCountry();

        useStoreToasts().loadToasts();
        useStorePaymentsNotification().subscribe(playerInfo.id);
        useStoreCrossSalePopup().syncPopups();

        return playerInfo;
      } else if (playerInfo?.errors) {
        this.status = ELoadingStatus.error;

        return { errors: playerInfo.errors };
      } else if (playerInfo?.required) {
        return { required: playerInfo.required };
      } else {
        this.status = ELoadingStatus.error;

        return { errors: { unauthorized: useStoreComponents().errorsMessages.unknownError } };
      }
    },

    /**
     * Action for sending request to SS API and changing user status to not loginned
     *
     * @param context { commit }
     */
    async signout(): Promise<boolean> {
      this.status = ELoadingStatus.loading;

      try {
        document.cookie = `profile_country=null;Max-Age=-99999999;`;
        await softswiss().delete({
          path: '/api/users/sign_out',
          force: true,
        });

        useCookieUserId().clear();

        useStoreRoot().SET_USER_CURRENCY('');
        useStoreTransactionHistory().resetState();

        this.playerInfo = {} as ISsPlayerResponse;
        this.playerCompPoints = {} as ISsPlayerCompPoints;
        this.status = ELoadingStatus.loaded;
        this.authStatusEarlyLoad = false;
        this.playerDocuments = [];
        this.isDocumentsLoadedOnce = false;
        this.playerTwoFactorData = {} as ISsPlayerTwoFactor;
        this.playerSessions = [];
        this.playerSubscriptionsStatuses = {} as ISsPlayerSubscriptions;
        this.deletePlayerInfoFromLocalStorage();

        localStorage.removeItem('playerBalance');

        useStoreSsCentrifuge().unsubscribeChannels();
        useStoreNotifications().unsubscribe();
        useStoreSsPlayerBonuses().stopCheckingUpdates();
        useStoreSsPlayer().stopCheckingCompointsUpdates();
        useStoreSsPlayerPayments().stopCheckingUpdates();
        softswiss().CLEAR_DATA();
        useOverlayQueueStore().removeFromOverlayQueue(EDropdownName.headerProfile);

        useStoreSsTournaments().resetState();

        useStoreSsPaymentMethods().resetCache();
        useStoreSessionTimer().clear();
        useStoreSsLimits().clearStore();
        useStorePaymentsNotification().unsubscribe();
        useCookieStag().removeStag();
        useStoreSsLocales().saveStag('161295_noclickid', useStoreStag().uniq_id);
        localStorage.setItem(HAD_SIGNIN_STORAGE_KEY, 'true');
        sessionStorage.removeItem(STORAGE_BONUS_ABUSER_KEY);
        this.setHasAuthToken(false);
        useCookieBonusType().init();
        useStoreSsLootboxes().resetState();
        this.playerHasAbuserStatus = false;
        logoutOneSignal();
        navigateTo(useStoreRoot().localePath('/'), { replace: true });

        return true;
      } catch {
        this.status = ELoadingStatus.error;
        return false;
      }
    },

    async addDefaultCurrencies() {
      const currentCurrency = String(this.playerCurrency);
      const currenciesToAdd = DEFAULT_CURRENCIES.filter((currency) => currency !== currentCurrency);

      for (const currency of currenciesToAdd) {
        await this.setPlayerCurrency(currency);
      }

      await useStoreSsAccounts().loadPlayerBalance();
      await this.setPlayerCurrency(currentCurrency);
    },

    async register(
      playerData: ISsPlayerRegister,
      options: {
        error?: any;
        headers?: IHttpRequestOptions['headers'];
        isMainPageSignup?: boolean;
      } = {}
    ): Promise<ISsPlayerRegistrationResponse> {
      this.status = ELoadingStatus.loading;

      if (options.error) {
        throw options.error;
      }
      const storeSeoReferrer = useStoreSeoReferrer();
      const playerInfo = await softswiss().post<ISsPlayerResponse>({
        path: '/api/users',
        cacheble: false,
        force: true,
        payload: { user: playerData } as ISsPlayerRegistrationRequest,
        getErrors: true,
        getRequired: true,
        headers: options.headers,
      });

      if (playerInfo?.id) {
        const isFraudEmailPromise = useStoreSeon().checkIfFraudEmailWithSeon(playerData.email);
        let isFraudEmail = false;
        const groupsToAdd = [];

        const processFraudEmail = async () => {
          isFraudEmail = await isFraudEmailPromise;

          if (isFraudEmail) {
            groupsToAdd.push(BONUS_ABUSER_GROUP);
            sessionStorage.setItem(STORAGE_BONUS_ABUSER_KEY, 'true');
          } else {
            sessionStorage.removeItem(STORAGE_BONUS_ABUSER_KEY);
          }
          this.playerHasAbuserStatus = true;
        };

        if (options.isMainPageSignup) {
          await processFraudEmail();
        }
        this.playerInfo = playerInfo;
        this.savePlayerInfoToLocalStorage(playerInfo);
        await this.loadCommonData({ isAfterRegistration: true });

        this.addDefaultCurrencies();
        this.loadUserCompPoints();

        if (!options.isMainPageSignup) {
          await processFraudEmail();
        }

        this.status = ELoadingStatus.loaded;

        if (storeSeoReferrer.isReferrerGroup) {
          groupsToAdd.push(storeSeoReferrer.referrerGroup);
        }

        const promoTypeCookie = getCookieValue('promo_type') as 'min_dep' | 'no_dep' | 'on_dep';
        if (promoTypeCookie && PROMO_TYPE_GROUPS[promoTypeCookie]) {
          groupsToAdd.push(PROMO_TYPE_GROUPS[promoTypeCookie]);
          useCookieBonusType().remove();
        }

        if (groupsToAdd.length) {
          this.updatePlayerGroups({ add: groupsToAdd });
        }

        await useStoreToasts().loadToasts();
        useStoreToasts().add(ETriggerId.RegistrationComplete);
        useStorePaymentsNotification().subscribe(playerInfo.id);
        useStoreCrossSalePopup().syncPopups();

        this.checkEstonianLicenceFromProfileCountry();
        useOneSignalID().setUserGroup('signup');
        return { playerInfo };
      } else if (playerInfo?.errors) {
        this.status = ELoadingStatus.error;

        return { playerInfo, errors: playerInfo.errors };
      }

      return {
        playerInfo: this.playerInfo,
        errors: { result: 'login failed' },
      };
    },

    async updateProfile(
      playerData: ISsPlayerProfileUpdate
    ): Promise<ISsPlayerRegistrationResponse | any> {
      const payload: ISsPlayerProfileUpdateRequest = {
        context: 'edition',
        player: playerData,
      };

      const playerInfo = await softswiss().patch<ISsPlayerResponse>({
        path: '/api/player',
        getErrors: true,
        cacheble: false,
        payload,
      });

      if (!playerInfo?.errors) {
        const updatePlayerInfo = await this.fetchPlayerData();

        this.playerInfo = { ...this.playerInfo, ...updatePlayerInfo };
        this.savePlayerInfoToLocalStorage(updatePlayerInfo);

        this.checkEstonianLicenceFromProfileCountry(true);

        return { playerInfo: this.playerInfo };
      } else {
        return { playerInfo, errors: playerInfo.errors };
      }
    },

    /**
     * Load user's settings from SS
     */
    async loadUserSettings(options?: {
      forAuthStatus?: boolean;
      force?: boolean;
      isAfterRegistration?: boolean;
    }) {
      try {
        let queryParams = '';
        if (
          options?.isAfterRegistration &&
          this.isPlayerAuthorised &&
          localStorage.getItem('ref_code')
        ) {
          queryParams = `?ref_code=${localStorage.getItem('ref_code')}`;
          localStorage.removeItem('ref_code');
        }
        const playerSettings = await softswiss().get<ISsPlayerSettings>({
          path: `/api/player/settings${queryParams}`,
          cacheble: false,
          force: options?.force,
        });

        if (playerSettings && !options?.forAuthStatus) {
          this.playerSettings = playerSettings;
        }

        return playerSettings;
      } catch (e: any) {
        console.error(e);
        return null;
      }
    },

    /**
     * save player default currency into SS API and store
     *
     * @param currency - currecncy code
     */
    async setPlayerCurrency(currency: string) {
      if (this.isPlayerAuthorised) {
        try {
          await softswiss().post<ISsPlayerBalance>({
            path: '/api/player/accounts',
            cacheble: false,
            payload: { currency },
          });

          this.playerInfo.currency = currency;

          const playerInfoToUpdate = this.getPlayerInfoFromLocalStorage();

          if (playerInfoToUpdate) {
            this.savePlayerInfoToLocalStorage({ ...playerInfoToUpdate, currency });
          }
        } catch (e: any) {
          console.error(e);
        }
      }
    },

    /**
     * Update all user's groupNames
     * @param ctx State context
     * @param body {UserGroupsUpdateRequestInterface} Data for update request,
     * contain ADD (optional) and REMOVE (optional) arrays
     */
    async updatePlayerGroups(body: ISsserGroupsUpdateRequestInterface): Promise<void> {
      try {
        const groups: ISsserGroupsUpdateRequestInterface = {};

        if (body.add?.length) {
          groups.add = body.add;
        }

        if (body.remove?.length) {
          groups.remove = body.remove;
        }

        await softswiss().post({
          path: '/api/player/groups',
          cacheble: false,
          payload: { groups },
        });
        await this.load();
      } catch (e: any) {
        console.error(new Error('update of groups failed!'), e);
      }
    },

    fetchDataCompoints() {
      if (!this.requestCompointsPromise) {
        this.requestCompointsPromise = softswiss()
          .get<ISsPlayerCompPoints>({
            path: '/api/player/comp_points',
            cacheble: false,
            force: true,
          })
          .finally(() => (this.requestCompointsPromise = null));
      }

      return this.requestCompointsPromise;
    },

    /**
     * Load user's comp points from SS
     * @param context { commit, dispatch }
     */
    async loadUserCompPoints(skipCheckingVipLevelModal?: boolean): Promise<void> {
      try {
        if (this.isPlayerAuthorised) {
          this.statusCompoints = ELoadingStatus.loading;

          const compoints = await this.fetchDataCompoints();

          if (compoints) {
            this.playerCompPoints = compoints;
            this.lastUpdateCompointsTime = Date.now();
          }
        }

        if (!skipCheckingVipLevelModal) {
          useStoreVipLevelData().openModalIfNeeded();
        }

        this.statusCompoints = ELoadingStatus.loaded;
      } catch (e: any) {
        console.error(e);
        this.statusCompoints = ELoadingStatus.error;
      }
    },

    /**
     * Updating comp points amount after getting new message in websockets
     * @param compoints - compoint amount
     */
    updateUserCompPoints(compoints: ISsPlayerCompPoints) {
      this.playerCompPoints = compoints;
      this.lastUpdateCompointsTime = Date.now();
      useStoreVipLevelData().openModalIfNeeded();
    },

    async startCheckingCompointsUpdates() {
      await this.loadUserCompPoints();

      this.intervalId = window.setInterval(() => {
        if (Date.now() - this.lastUpdateCompointsTime > COMMON_REQUEST_INTERVAL) {
          this.loadUserCompPoints();
        }
      }, COMMON_REQUEST_INTERVAL);
    },

    stopCheckingCompointsUpdates() {
      window.clearInterval(this.intervalId);
    },

    /**
     * Resend email for confirmation notification players
     * and write to localStorage data send
     *
     * @param email - email to which to send confirmation
     */
    async resendConfirmationEmail(email: string): Promise<boolean> {
      try {
        const confirmation = await softswiss().post({
          path: '/api/users/confirmation',
          force: true,
          payload: { user: { email } },
        });

        return !!confirmation;
      } catch (e: any) {
        console.error(e);
        return false;
      }
    },

    /**
     * Player's email confirmation
     *
     * @param confirmation_token - token from user's confirmation email
     */
    async confirmEmail(confirmation_token: string): Promise<ISsPlayerEmailConfirmationResponse> {
      try {
        const confirmation = await softswiss().get({
          path: `/api/users/confirmation`,
          params: { confirmation_token } as { [key: string]: string },
          getErrors: true,
        });

        if (confirmation?.id) {
          return { result: true };
        } else {
          return { result: false, reason: String(confirmation?.errors?.email) };
        }
      } catch (e: any) {
        console.error(e);
        return { result: false };
      }
    },

    /**
     *
     * Player phone verification
     ********************************************************
     */
    async playerPhoneSendCode(number: string) {
      try {
        const playerSendCode = await softswiss().post({
          path: '/api/player/phone',
          payload: { phone: { number } },
          getErrors: true,
        });

        this.playerPhoneInfo = playerSendCode;

        return playerSendCode;
      } catch (e: any) {
        console.error(e);
        return null;
      }
    },
    async getPlayerPhoneInfo() {
      try {
        const playerInfoPhone = (await softswiss().get({
          path: '/api/player/phone',
          force: true,
        })) as {
          phone_country: string;
          phone_number: string;
          sms_limit_reached: boolean;
          verified_status: string;
        }[];

        this.playerPhoneInfo = playerInfoPhone[0] || {};

        return playerInfoPhone[0];
      } catch (e: any) {
        console.error(e);
        return null;
      }
    },
    async playerPhoneConfirm(code: string) {
      try {
        const playerConfirm = await softswiss().post({
          path: '/api/player/phone/confirm',
          payload: { phone: { code } },
          getErrors: true,
        });

        return playerConfirm;
      } catch (e: any) {
        console.error(e);
        return null;
      }
    },
    async playerResendVerificationCode() {
      try {
        const playerResend = await softswiss().post({
          path: '/api/player/phone/resend_verification_code',
          getErrors: true,
        });

        if (!playerResend.errors) {
          this.playerPhoneInfo = playerResend || {};
        }

        return playerResend;
      } catch (e: any) {
        console.error(e);
        return null;
      }
    },
    async playerPhoneDelete() {
      try {
        const deletePlayerPhone = await softswiss().delete({
          path: '/api/player/phone',
        });
        this.playerInfo.mobile_phone = '';
        return deletePlayerPhone;
      } catch (e: any) {
        console.error(e);
        return null;
      }
    },
    /**
     * Player's account unlock
     *
     * @param unlock_token - token for account's unlock from user's email
     */
    async unlockAccount(unlock_token: string): Promise<boolean> {
      try {
        const unlockUser = await softswiss().get({
          path: `/api/users/unlock`,
          params: { unlock_token } as { [key: string]: string },
        });

        return !!unlockUser?.id;
      } catch (e: any) {
        console.error(e);
        return false;
      }
    },
    async checkCoolingOffPeriod() {
      const userLimits: ISsPlayerLimits[] | null = await softswiss().get({
        path: '/api/user_limits',
        force: true,
      });

      const coolingOffPeriod = userLimits?.find((limit) => (limit.type = 'coolingoff'));

      return (
        coolingOffPeriod?.strategy === 'access' &&
        (coolingOffPeriod?.status === 'active' ||
          coolingOffPeriod?.status === 'unconfirmed_disable')
      );
    },
    async loadOAuthRoutes() {
      const oAuthRoutes: IOAuthRoutes | null = await softswiss().get({
        path: '/api/info/auth_providers_routes',
      });

      if (oAuthRoutes) {
        this.oAuthRoutes = oAuthRoutes;
      }
    },

    async findIfGoogleLogin() {
      const response: IOAuthProvider[] | null = await softswiss().get({
        path: '/api/auth_providers',
      });

      return (
        !!response && response.some((provider) => provider.type === 'google' && provider.active)
      );
    },
    async updateTermsAcceptance() {
      const additionalData: { terms_acceptance: boolean; country: string; nickname?: string } = {
        terms_acceptance: true,
        country: useStoreCountry().country,
      };
      if (this.playerInfo.auth_fields_missed?.includes('nickname')) {
        additionalData.nickname = this.playerInfo.email?.substring(0, 4) + '****' || '';
      }
      await softswiss().post({
        path: '/api/auth_providers/update_details',
        payload: {
          user: {
            profile_attributes: additionalData,
          },
        },
      });
    },

    updateGroups(data: ISsCentrifugeGroupsChannel[]) {
      const statuses = data.map((d) => ({ id: d.id, name: d.name }));
      const playerInfoToUpdate = this.getPlayerInfoFromLocalStorage();

      this.playerInfo.statuses = statuses;

      if (playerInfoToUpdate) {
        this.savePlayerInfoToLocalStorage({ ...playerInfoToUpdate, statuses });
      }
    },

    savePlayerInfoToLocalStorage(playerInfo: ISsPlayerResponse) {
      if (!window.localStorage || !playerInfo.id) {
        return;
      }

      window.localStorage.setItem('playerData', JSON.stringify(playerInfo));
    },

    getPlayerInfoFromLocalStorage(): ISsPlayerResponse | null {
      if (!window.localStorage) {
        return null;
      }

      const playerData = window.localStorage.getItem('playerData');

      return playerData ? JSON.parse(playerData) : null;
    },

    deletePlayerInfoFromLocalStorage() {
      if (!window.localStorage) {
        return;
      }
      window.localStorage.removeItem('playerData');
    },
    checkEstonianLicenceFromProfileCountry(fromRegister?: boolean) {
      const country = this.playerInfo?.country;
      if (!country) {
        return;
      }
      document.cookie = `profile_country=${country}`;

      if ((country === 'EE' || country === 'FI') && !useStoreRoot().isEstonianLicense) {
        if (fromRegister) {
          window.location.href = 'https://' + window.location.host + '?modal=estonian';
        } else {
          window.location.href = 'https://' + window.location.host;
        }
      }
    },
    async getPlayerSubscriptions(options?: { force?: boolean }) {
      const data = await softswiss().get<ISsPlayerSubscriptions>({
        path: '/api/subscriptions',
        cacheble: false,
        force: options?.force,
      });
      this.playerSubscriptionsStatuses = data || ({} as ISsPlayerSubscriptions);
    },
    async changePlayerSubscription(data: Partial<Record<keyof ISsPlayerSubscriptions, boolean>>) {
      await softswiss().put<ISsPlayerSubscriptions>({
        path: '/api/subscriptions',
        cacheble: false,
        payload: {
          subscription_params: data,
        },
      });
      await this.getPlayerSubscriptions({ force: true });
    },

    // DOCS
    async getPlayerDocuments(options?: { force?: boolean }) {
      const docs = await softswiss().get<ISsPlayerDocument[]>({
        path: '/api/player/documents',
        cacheble: false,
        force: options?.force,
      });
      this.playerDocuments = docs || [];
      this.isDocumentsLoadedOnce = true;
    },
    async addPlayerDocument(data: ISsPlayerUploadDocument) {
      const formData = new FormData();
      formData.append('document[attachment]', data.attachment);
      formData.append('document[description]', data.description);
      formData.append('document[document_type]', data.document_type);

      const uploadedDocument = await softswiss().post<tSsPlayerDocumentUploadResponse>({
        path: '/api/player/documents',
        payload: formData,
        getErrors: true,
        headers: {
          Accept: 'multipart/form-data',
          'Content-Type': 'multipart/form-data',
        },
      });
      if (uploadedDocument === null || 'errors' in uploadedDocument) {
        return false;
      }
      await this.getPlayerDocuments({ force: true });
      return uploadedDocument;
    },
    async removePlayerDocument(documentId: number) {
      const response = await softswiss().delete({
        path: `/api/player/documents/${documentId}`,
      });
      if (response === null) {
        return false;
      }
      await this.getPlayerDocuments({ force: true });
      return true;
    },
    async updatePlayerDocument(documentId: number, data: Partial<ISsPlayerUploadDocument>) {
      const formData = new FormData();

      for (const [key, value] of Object.entries(data)) {
        if (value !== undefined && value !== null) {
          formData.append(`document[${key}]`, value);
        }
      }

      const response = await softswiss().patch({
        path: `/api/player/documents/${documentId}`,
        payload: formData,
      });
      if (response === null) {
        return false;
      }
      await this.getPlayerDocuments();
      return true;
    },

    // security
    async getTwoFactorData(options?: { force?: boolean }) {
      const data = await softswiss().get<ISsPlayerTwoFactor>({
        path: '/api/player/two_factor',
        cacheble: false,
        force: options?.force,
      });
      this.playerTwoFactorData = data || ({} as ISsPlayerTwoFactor);
    },
    async activateTwoFactor(code: string) {
      const response = await softswiss().post({
        path: '/api/player/two_factor',
        payload: {
          otp_secret: this.playerTwoFactorData.otp_secret,
          authentication_code: code,
        },
        getErrors: true,
      });
      if (response.errors) {
        const errorsKeys = errorsParser(response.errors);
        return {
          errors: errorsKeys,
        };
      }

      if (response.backup_codes) {
        this.playerTwoFactorBackupCodes = response.backup_codes;
      }

      await this.loadPlayerInfo();
      return response;
    },
    async deactivateTwoFactor(code: string) {
      const response = await softswiss().delete({
        path: '/api/player/two_factor',
        payload: {
          two_factor: {
            authentication_code: code,
          },
        },
        getErrors: true,
      });

      if (response.errors) {
        const errorsKeys = errorsParser(response.errors);
        return {
          errors: errorsKeys,
        };
      }
      await this.getTwoFactorData({ force: true });
      await this.loadPlayerInfo();
      return true;
    },
    async updateUserPassword(data: {
      current_password: string;
      password: string;
      password_confirmation: string;
    }) {
      const response = await softswiss().put({
        path: '/api/users',
        payload: {
          user: data,
        },
        getErrors: true,
      });
      if (response?.errors) {
        const errorsKeys = errorsParser(response.errors);
        return {
          success: false,
          errors: errorsKeys,
        };
      }
      return { success: true };
    },
    async getUserSessions(options?: { force?: boolean }) {
      const activeSessions = await softswiss().get<ISsPlayerSession[]>({
        path: '/api/player/sessions',
        cacheble: false,
        force: options?.force,
      });
      const closedSessions = await softswiss().get<ISsPlayerSession[]>({
        path: '/api/player/sessions/closed',
        cacheble: false,
        force: options?.force,
      });
      this.playerSessions = [...(activeSessions || []), ...(closedSessions || [])];
    },
    async closeUserSession(id: number) {
      const response = await softswiss().delete<ISsPlayerSession[]>({
        path: `/api/player/sessions/${id}`,
        getErrors: true,
      });
      if (response.errors) {
        return false;
      }
      await this.getUserSessions({ force: true });
      return true;
    },
    async sendUserReport() {
      const response = await softswiss().post({
        path: `/api/gdpr_reports`,
        getErrors: true,
      });
      if (response?.errors) {
        const errorsKeys = errorsParser(response.errors);
        return {
          success: false,
          errors: errorsKeys,
        };
      }
      return { success: true };
    },
    loadSettingsCommonData() {
      Promise.all([
        this.getPlayerPhoneInfo(),
        this.getPlayerSubscriptions(),
        this.getTwoFactorData(),
      ]);
    },
  },
});
